import { Button } from '@aod/ui/components/ui/button';
import { ScrollArea } from '@aod/ui/components/ui/scroll-area';
import { Separator } from '@aod/ui/components/ui/separator';
import { useT } from '../../i18n/index.js';
import { artifactScene } from '../sceneEvents.js';
import { sceneUrls } from '../scenes.js';

/** The realm's relic picture (art audit P4), or null for an unknown realm. */
function artifactThumb(archetype: Artifact['archetype']): string | null {
  const scene = artifactScene(archetype);
  return scene ? sceneUrls(scene.group, scene.id).srcSmall : null;
}

const ARCHETYPE_CONFIG = {
  industry: { color: '#E67E22', icon: '⚙' },
  science: { color: '#3498DB', icon: '🔬' },
  faith: { color: '#F1C40F', icon: '✝' },
  arcane: { color: '#9B59B6', icon: '✦' },
  nature: { color: '#27AE60', icon: '❧' },
  chaos: { color: '#E74C3C', icon: '⦿' },
} as const;

export interface Artifact {
  id: string;
  archetype: keyof typeof ARCHETYPE_CONFIG;
  installed: boolean;
  installedIn?: string;
}

/** Archetype hue pulled toward the foreground so it reads on light and dark cards. */
function textTone(color: string): string {
  return `color-mix(in oklch, ${color} 65%, var(--foreground))`;
}

interface ArtifactsPanelProps {
  readonly artifacts: Artifact[];
  readonly onInstall: (artifactId: string) => void;
}

export function ArtifactsPanel({ artifacts, onInstall }: ArtifactsPanelProps) {
  const t = useT();
  const collected = artifacts.filter((a) => !a.installed);
  const installed = artifacts.filter((a) => a.installed);

  return (
    <section
      aria-label={t('doctrine.tab.artifacts')}
      className="flex min-h-0 flex-1 flex-col gap-2 text-foreground"
    >
      <PanelHeader collected={collected.length} installed={installed.length} />
      <Separator />
      <ScrollArea className="-mx-1 min-h-0 flex-1 px-1">
        <div className="flex flex-col gap-2 py-1">
          {collected.length === 0 && installed.length === 0 && (
            <p className="px-1 py-4 text-center text-xs text-muted-foreground italic">
              {t('artifacts.empty')}
            </p>
          )}
          {collected.map((artifact) => {
            const config = ARCHETYPE_CONFIG[artifact.archetype];
            return (
              <CollectedArtifactCard
                key={artifact.id}
                artifact={artifact}
                config={config}
                onInstall={onInstall}
              />
            );
          })}
          {installed.length > 0 && (
            <>
              <div className="px-1 pt-2 pb-1">
                <span className="text-[10px] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                  {t('artifacts.installed')}
                </span>
              </div>
              {installed.map((artifact) => {
                const config = ARCHETYPE_CONFIG[artifact.archetype];
                return (
                  <InstalledArtifactCard key={artifact.id} artifact={artifact} config={config} />
                );
              })}
            </>
          )}
        </div>
      </ScrollArea>
    </section>
  );
}

interface PanelHeaderProps {
  readonly collected: number;
  readonly installed: number;
}

function PanelHeader({ collected, installed }: PanelHeaderProps) {
  const t = useT();
  return (
    <header className="flex flex-col gap-1 px-1 pt-1 pb-2">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground">
          {t('artifacts.title')}
        </span>
        {(collected > 0 || installed > 0) && (
          <span className="font-mono text-[10px] text-muted-foreground">
            {collected} / {installed}
          </span>
        )}
      </div>
      {collected > 0 && (
        <p className="text-xs text-muted-foreground">
          {t('artifacts.collected', { count: collected })}
        </p>
      )}
    </header>
  );
}

interface CollectedArtifactCardProps {
  readonly artifact: Artifact;
  readonly config: (typeof ARCHETYPE_CONFIG)[keyof typeof ARCHETYPE_CONFIG];
  readonly onInstall: (artifactId: string) => void;
}

function CollectedArtifactCard({ artifact, config, onInstall }: CollectedArtifactCardProps) {
  const t = useT();
  const thumb = artifactThumb(artifact.archetype);
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-card/60 p-2">
      {thumb ? (
        <img
          src={thumb}
          alt=""
          loading="lazy"
          className="h-8 w-8 shrink-0 rounded-md border object-cover"
          style={{ borderColor: `${config.color}45` }}
          data-testid="artifact-scene"
        />
      ) : (
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-base"
          style={{ backgroundColor: `${config.color}25`, color: config.color }}
        >
          {config.icon}
        </span>
      )}
      <div className="flex flex-1 flex-col gap-0.5">
        <span className="text-xs font-medium" style={{ color: textTone(config.color) }}>
          {t(`archetype.${artifact.archetype}`)}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="h-5 w-full text-[10px] tracking-wide"
          onClick={() => onInstall(artifact.id)}
        >
          {t('artifacts.install')}
        </Button>
      </div>
    </div>
  );
}

interface InstalledArtifactCardProps {
  readonly artifact: Artifact;
  readonly config: (typeof ARCHETYPE_CONFIG)[keyof typeof ARCHETYPE_CONFIG];
}

function InstalledArtifactCard({ artifact, config }: InstalledArtifactCardProps) {
  const t = useT();
  const thumb = artifactThumb(artifact.archetype);
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border/30 bg-card/40 p-2 opacity-60">
      {thumb ? (
        <img
          src={thumb}
          alt=""
          loading="lazy"
          className="h-8 w-8 shrink-0 rounded-md border object-cover"
          style={{ borderColor: `${config.color}40` }}
          data-testid="artifact-scene"
        />
      ) : (
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-base"
          style={{ backgroundColor: `${config.color}20`, color: config.color }}
        >
          {config.icon}
        </span>
      )}
      <div className="flex flex-1 flex-col gap-0.5">
        <span className="text-xs font-medium" style={{ color: textTone(config.color) }}>
          {t(`archetype.${artifact.archetype}`)}
        </span>
        <span className="text-[10px] text-muted-foreground">
          {t('artifacts.installedIn', { city: artifact.installedIn ?? t('common.unknown') })}
        </span>
      </div>
    </div>
  );
}
